import { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { toast } from "sonner";

// Relacart DISCOVER USB IDs
const RELACART_VID = 0x0F8B;
const RELACART_PID = 0x0051;

// tinySA Serial settings
const TINYSA_BAUD_RATE = 115200;

// Check if Web Serial API is supported
const isWebSerialSupported = () => 'serial' in navigator;

const USBConnectionContext = createContext(null);

export function USBConnectionProvider({ children }) {
  // ============ HID Connection state (Relacart) ============
  const [hidConnected, setHidConnected] = useState(false);
  const [hidDevice, setHidDevice] = useState(null);
  const [hidDeviceInfo, setHidDeviceInfo] = useState(null);
  const [hidScanning, setHidScanning] = useState(false);
  
  // ============ Serial Connection state (tinySA) ============
  const [serialConnected, setSerialConnected] = useState(false);
  const [serialPort, setSerialPort] = useState(null);
  const [serialScanning, setSerialScanning] = useState(false);
  const [serialDeviceInfo, setSerialDeviceInfo] = useState(null);
  const [tinySAData, setTinySAData] = useState([]);
  
  // ============ Persistent scan history ============
  const [lastScanResult, setLastScanResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  
  // ============ Coordination Mode State ============
  const [coordinationMode, setCoordinationMode] = useState(false);
  const [coordinationData, setCoordinationData] = useState({
    startTime: null,
    scanCount: 0,
    // Accumulated data per frequency point
    frequencyStats: {}, // { freq: { sum, count, max, min, values[] } }
    // Timeline markers for significant events
    markers: [], // { time, type, frequency, description }
    // Analysis results
    analysis: null,
  });
  
  // ============ Shared state ============
  const [activeDevice, setActiveDevice] = useState(null);
  const [scanData, setScanData] = useState([]);
  const [rawDataLog, setRawDataLog] = useState([]);
  
  // Refs for device handles - these persist across re-renders
  const hidDeviceRef = useRef(null);
  const serialPortRef = useRef(null);
  const serialReaderRef = useRef(null);
  const serialWriterRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const reconnectAttemptedRef = useRef(false);
  
  // Check API support
  const isHIDSupported = typeof navigator !== 'undefined' && 'hid' in navigator;
  const isSerialSupported = typeof navigator !== 'undefined' && 'serial' in navigator;

  // ============ Auto-reconnect to previously authorized ports ============
  useEffect(() => {
    const autoReconnect = async () => {
      // Only try once per session
      if (reconnectAttemptedRef.current) return;
      reconnectAttemptedRef.current = true;
      
      if (!isSerialSupported) return;
      
      try {
        // Get list of previously authorized ports
        const ports = await navigator.serial.getPorts();
        
        if (ports.length > 0) {
          // Try to reconnect to the first available port
          const port = ports[0];
          const info = port.getInfo();
          
          // Check if it's a known device type
          const knownVendors = [0x0483, 0x1A86, 0x10C4, 0x0403, 0x067B];
          if (knownVendors.includes(info.usbVendorId)) {
            try {
              // Check if port is already open
              if (port.readable && port.writable) {
                console.log("Port already open from previous session");
              } else {
                await port.open({ baudRate: TINYSA_BAUD_RATE });
              }
              
              setSerialDeviceInfo({
                vendorId: info.usbVendorId?.toString(16).toUpperCase().padStart(4, '0') || 'N/A',
                productId: info.usbProductId?.toString(16).toUpperCase().padStart(4, '0') || 'N/A',
                autoReconnected: true
              });
              
              serialPortRef.current = port;
              setSerialPort(port);
              setSerialConnected(true);
              setActiveDevice('tinysa');
              
              toast.success("tinySA reconectado automáticamente", {
                description: "La conexión anterior se ha restaurado"
              });
            } catch (openErr) {
              // Port might already be open or in use by another app
              console.log("Auto-reconnect: port issue", openErr.message);
            }
          }
        }
      } catch (err) {
        console.log("Auto-reconnect check failed:", err);
      }
    };
    
    // Delay auto-reconnect to let the app initialize
    const timer = setTimeout(autoReconnect, 1000);
    return () => clearTimeout(timer);
  }, [isSerialSupported]);

  // ============ Handle port disconnect events ============
  useEffect(() => {
    if (!isSerialSupported) return;
    
    const handleDisconnect = (event) => {
      if (serialPortRef.current === event.target) {
        setSerialConnected(false);
        setSerialPort(null);
        setSerialScanning(false);
        serialPortRef.current = null;
        if (activeDevice === 'tinysa') setActiveDevice(null);
        
        toast.warning("tinySA desconectado", {
          description: "El dispositivo fue desconectado físicamente"
        });
      }
    };
    
    navigator.serial.addEventListener('disconnect', handleDisconnect);
    return () => navigator.serial.removeEventListener('disconnect', handleDisconnect);
  }, [isSerialSupported, activeDevice]);

  // Process incoming HID data
  const processHIDData = useCallback((bytes, reportId) => {
    const hexString = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join(' ');
    const asciiString = Array.from(bytes).map(b => b >= 32 && b < 127 ? String.fromCharCode(b) : '.').join('');
    
    const startIdx = asciiString.indexOf('%');
    const endIdx = asciiString.indexOf('#');
    const message = startIdx >= 0 && endIdx > startIdx ? asciiString.substring(startIdx, endIdx + 1) : asciiString;
    
    // Log raw data
    setRawDataLog(prev => {
      const newEntry = {
        time: new Date().toLocaleTimeString(),
        reportId,
        length: bytes.length,
        hex: hexString.substring(0, 60) + (hexString.length > 60 ? '...' : ''),
        ascii: message.substring(0, 50)
      };
      return [...prev.slice(-20), newEntry];
    });
    
    // Parse Relacart protocol messages
    if (message.startsWith('%') && message.endsWith('#')) {
      const content = message.substring(1, message.length - 1);
      const parts = content.split(',');
      const cmd = parts[0];
      
      if (cmd === 'H') {
        // Heartbeat
        const serial = parts[2] || '';
        const counter = parts[3] || '0000';
        setHidDeviceInfo({
          serial,
          counter,
          lastSeen: new Date().toLocaleTimeString()
        });
      }
    }
    
    // Try to extract frequency/level data
    const freqLevelMatch = asciiString.match(/(\d{3,4})[,.\s]+(-?\d{1,3})/g);
    if (freqLevelMatch) {
      freqLevelMatch.forEach(match => {
        const [freqStr, levelStr] = match.split(/[,.\s]+/);
        let freq = parseFloat(freqStr);
        const level = parseFloat(levelStr);
        
        if (freq > 100000) freq = freq / 1000000;
        else if (freq > 1000) freq = freq / 1000;
        
        if (freq >= 200 && freq <= 1200 && level > -130 && level < 10) {
          setScanData(prev => {
            const existingIndex = prev.findIndex(p => Math.abs(p.frequency - freq) < 0.5);
            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = { frequency: freq, level, timestamp: Date.now() };
              return updated;
            }
            return [...prev, { frequency: freq, level, timestamp: Date.now() }].sort((a, b) => a.frequency - b.frequency);
          });
        }
      });
    }
  }, []);

  // Send command to HID device
  const sendHIDCommand = useCallback(async (command) => {
    if (!hidDeviceRef.current || !hidDeviceRef.current.opened) {
      return false;
    }
    
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(command);
      const paddedData = new Uint8Array(64);
      paddedData.set(data);
      
      await hidDeviceRef.current.sendReport(0, paddedData);
      
      setRawDataLog(prev => [...prev.slice(-19), {
        time: new Date().toLocaleTimeString(),
        reportId: 'TX',
        length: command.length,
        hex: '>> ENVIADO',
        ascii: command
      }]);
      
      return true;
    } catch (err) {
      console.error("Failed to send HID command:", err);
      return false;
    }
  }, []);

  // Connect to Relacart HID device
  const connectHID = useCallback(async (useFilter = true) => {
    if (!isHIDSupported) {
      toast.error("Tu navegador no soporta WebHID. Usa Chrome/Edge.");
      return false;
    }

    try {
      const filters = useFilter 
        ? [{ vendorId: RELACART_VID, productId: RELACART_PID }, { vendorId: RELACART_VID }]
        : [];
      
      const devices = await navigator.hid.requestDevice({ filters });

      if (devices.length === 0) {
        toast.error("No se seleccionó ningún dispositivo");
        return false;
      }

      const device = devices[0];
      
      if (!device.opened) {
        await device.open();
      }
      
      setHidDevice({
        name: device.productName || "Relacart U.DISCOVER",
        vendorId: device.vendorId.toString(16).toUpperCase().padStart(4, '0'),
        productId: device.productId.toString(16).toUpperCase().padStart(4, '0'),
      });
      
      hidDeviceRef.current = device;

      // Set up input report listener
      device.addEventListener('inputreport', (event) => {
        const { data, reportId } = event;
        const bytes = new Uint8Array(data.buffer);
        processHIDData(bytes, reportId);
      });

      // Handle disconnect
      navigator.hid.addEventListener('disconnect', (event) => {
        if (event.device === hidDeviceRef.current) {
          setHidConnected(false);
          setHidDevice(null);
          setHidScanning(false);
          hidDeviceRef.current = null;
          toast.info("Dispositivo HID desconectado");
        }
      });

      setHidConnected(true);
      setActiveDevice('relacart');
      toast.success(`${device.productName || 'Dispositivo HID'} conectado`);
      return true;

    } catch (err) {
      console.error("HID connection error:", err);
      if (err.name !== 'NotFoundError') {
        toast.error("Error: " + (err.message || "No se pudo conectar"));
      }
      return false;
    }
  }, [isHIDSupported, processHIDData]);

  // Disconnect HID device
  const disconnectHID = useCallback(async () => {
    try {
      if (hidDeviceRef.current) {
        await hidDeviceRef.current.close();
      }
    } catch (error) {
      console.error("Disconnect error:", error);
    }
    
    setHidConnected(false);
    setHidDevice(null);
    setHidDeviceInfo(null);
    setHidScanning(false);
    hidDeviceRef.current = null;
    if (activeDevice === 'relacart') setActiveDevice(null);
  }, [activeDevice]);

  // Start scanning (HID)
  const startHIDScan = useCallback(async () => {
    if (!hidConnected) return;
    
    setHidScanning(true);
    setScanData([]);
    
    const counter = hidDeviceInfo?.counter || '0000';
    await sendHIDCommand(`%A,00,${counter}#`);
    await new Promise(r => setTimeout(r, 500));
    await sendHIDCommand("%S,00#");
    await sendHIDCommand("%D,00#");
  }, [hidConnected, hidDeviceInfo, sendHIDCommand]);

  // Stop scanning (HID)
  const stopHIDScan = useCallback(async () => {
    setHidScanning(false);
    await sendHIDCommand("%X,00#");
  }, [sendHIDCommand]);

  // Clear scan data
  const clearScanData = useCallback(() => {
    setScanData([]);
    setRawDataLog([]);
  }, []);

  // Import scan data from CSV
  const importScanData = useCallback((data) => {
    setScanData(data);
  }, []);

  // Save current scan to history (persists across page navigation)
  const saveScanToHistory = useCallback((name = null) => {
    if (scanData.length === 0 && tinySAData.length === 0) {
      toast.error("No hay datos de escaneo para guardar");
      return null;
    }
    
    const dataToSave = tinySAData.length > 0 ? tinySAData : scanData;
    const scanEntry = {
      id: Date.now(),
      name: name || `Escaneo ${new Date().toLocaleString()}`,
      timestamp: new Date().toISOString(),
      data: dataToSave,
      device: activeDevice,
      pointCount: dataToSave.length
    };
    
    setScanHistory(prev => [...prev.slice(-9), scanEntry]); // Keep last 10
    setLastScanResult(scanEntry);
    
    return scanEntry;
  }, [scanData, tinySAData, activeDevice]);

  // Load scan from history
  const loadScanFromHistory = useCallback((scanId) => {
    const scan = scanHistory.find(s => s.id === scanId);
    if (scan) {
      setScanData(scan.data);
      setTinySAData(scan.data);
      toast.success(`Cargado: ${scan.name}`);
      return scan;
    }
    return null;
  }, [scanHistory]);

  // Clear scan history
  const clearScanHistory = useCallback(() => {
    setScanHistory([]);
    setLastScanResult(null);
  }, []);

  // ============ SERIAL (tinySA) Functions ============
  
  // Connect to tinySA via Web Serial API
  const connectSerial = useCallback(async () => {
    if (!isSerialSupported) {
      toast.error("Tu navegador no soporta Web Serial. Usa Chrome/Edge en desktop.");
      return false;
    }

    // First, try to close any existing connection
    try {
      if (serialPortRef.current) {
        if (serialReaderRef.current) {
          try { await serialReaderRef.current.cancel(); } catch(e) {}
          serialReaderRef.current = null;
        }
        if (serialWriterRef.current) {
          try { await serialWriterRef.current.close(); } catch(e) {}
          serialWriterRef.current = null;
        }
        try { await serialPortRef.current.close(); } catch(e) {}
        serialPortRef.current = null;
      }
    } catch (e) {
      console.log("Cleanup before connect:", e);
    }

    try {
      const port = await navigator.serial.requestPort({
        filters: [
          { usbVendorId: 0x0483 }, // STM32
          { usbVendorId: 0x1A86 }, // CH340
          { usbVendorId: 0x10C4 }, // CP210x
          { usbVendorId: 0x0403 }, // FTDI
          { usbVendorId: 0x067B }, // Prolific
        ]
      });

      // Check if port is already open
      if (port.readable && port.writable) {
        // Port is already open, just use it
        console.log("Port already open, reusing...");
      } else {
        await port.open({ baudRate: TINYSA_BAUD_RATE });
      }
      
      const info = port.getInfo();
      setSerialDeviceInfo({
        vendorId: info.usbVendorId?.toString(16).toUpperCase().padStart(4, '0') || 'N/A',
        productId: info.usbProductId?.toString(16).toUpperCase().padStart(4, '0') || 'N/A',
      });
      
      serialPortRef.current = port;
      setSerialPort(port);
      setSerialConnected(true);
      setActiveDevice('tinysa');
      
      toast.success("tinySA conectado");
      return true;
    } catch (err) {
      if (err.name === 'InvalidStateError' || err.message?.includes('already open')) {
        toast.error("El puerto serial está ocupado. Cierra otras aplicaciones que usen el tinySA (tinySA-App, etc.)");
      } else if (err.name !== 'NotFoundError') {
        console.error("Serial connection error:", err);
        toast.error("Error: " + (err.message || "No se pudo conectar"));
      }
      return false;
    }
  }, [isSerialSupported]);

  // Disconnect Serial
  const disconnectSerial = useCallback(async () => {
    // Stop scanning first
    scanningActiveRef.current = false;
    
    try {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      
      if (serialReaderRef.current) {
        try { await serialReaderRef.current.cancel(); } catch(e) {}
        try { serialReaderRef.current.releaseLock(); } catch(e) {}
        serialReaderRef.current = null;
      }
      
      if (serialWriterRef.current) {
        try { serialWriterRef.current.releaseLock(); } catch(e) {}
        serialWriterRef.current = null;
      }
      
      if (serialPortRef.current) {
        try { await serialPortRef.current.close(); } catch(e) {}
      }
    } catch (err) {
      console.error("Serial disconnect error:", err);
    }
    
    serialPortRef.current = null;
    setSerialPort(null);
    setSerialConnected(false);
    setSerialScanning(false);
    setSerialDeviceInfo(null);
    if (activeDevice === 'tinysa') setActiveDevice(null);
    
    toast.info("tinySA desconectado");
  }, [activeDevice]);

  // Ref to track if scanning should continue
  const scanningActiveRef = useRef(false);

  // Start tinySA scanning
  const startSerialScan = useCallback(async () => {
    if (!serialConnected || !serialPortRef.current) {
      toast.error("tinySA no conectado");
      return;
    }

    // If already scanning, don't start again
    if (scanningActiveRef.current) {
      console.log("Scan already active");
      return;
    }
    
    try {
      // Release any existing locks first
      if (serialReaderRef.current) {
        try {
          await serialReaderRef.current.cancel();
          serialReaderRef.current.releaseLock();
        } catch (e) {
          console.log("Reader cleanup:", e.message);
        }
        serialReaderRef.current = null;
      }
      
      if (serialWriterRef.current) {
        try {
          serialWriterRef.current.releaseLock();
        } catch (e) {
          console.log("Writer cleanup:", e.message);
        }
        serialWriterRef.current = null;
      }
      
      // Small delay after releasing locks
      await new Promise(r => setTimeout(r, 100));
      
      // Get fresh reader/writer
      const writer = serialPortRef.current.writable.getWriter();
      const reader = serialPortRef.current.readable.getReader();
      
      serialWriterRef.current = writer;
      serialReaderRef.current = reader;
      
      const encoder = new TextEncoder();
      
      console.log("[tinySA] Sending sweep command...");
      
      // Setup sweep first
      await writer.write(encoder.encode("sweep 480000000 980000000\r"));
      await new Promise(r => setTimeout(r, 300));
      
      // Request initial data
      console.log("[tinySA] Sending data 0 command...");
      await writer.write(encoder.encode("data 0\r"));
      
      scanningActiveRef.current = true;
      setSerialScanning(true);
      setTinySAData([]);
      
      toast.success("Escaneo iniciado", { description: "El escaneo continuará aunque cambies de página" });
      
      // Read loop - runs in background
      const readLoop = async () => {
        let dataBuffer = []; // Buffer for scientific notation values
        let textBuffer = "";
        let lastLogTime = 0;
        
        console.log("[tinySA] Read loop started");
        
        try {
          while (scanningActiveRef.current) {
            const { value, done } = await reader.read();
            if (done) {
              console.log("[tinySA] Reader done");
              break;
            }
            
            const text = new TextDecoder().decode(value);
            textBuffer += text;
            
            // Log raw data periodically (every 2 seconds)
            const now = Date.now();
            if (now - lastLogTime > 2000) {
              console.log("[tinySA] Raw data sample:", text.substring(0, 100));
              lastLogTime = now;
            }
            
            // Check for prompt indicating response complete
            const hasPrompt = textBuffer.includes("ch>");
            
            // Process lines (tinySA uses \r as line separator)
            const lines = textBuffer.split(/[\r\n]+/);
            textBuffer = lines.pop() || "";
            
            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed || trimmed === 'ch>') continue;
              
              // Parse scientific notation: -8.706250e+01 (tinySA "data 0" response)
              if (/^-?\d+\.\d+e[+-]\d+$/i.test(trimmed)) {
                const dBmValue = parseFloat(trimmed);
                if (!isNaN(dBmValue) && dBmValue < 0 && dBmValue > -150) {
                  dataBuffer.push(dBmValue);
                }
              }
            }
            
            // When we have enough data points and see prompt, update display
            if (hasPrompt && dataBuffer.length >= 100) {
              const startFreq = 480;
              const endFreq = 980;
              const numPoints = dataBuffer.length;
              const stepFreq = (endFreq - startFreq) / (numPoints - 1);
              
              const newData = dataBuffer.map((level, i) => ({
                frequency: startFreq + (i * stepFreq),
                level: level
              }));
              
              setTinySAData(newData);
              console.log(`[tinySA] Updated chart: ${numPoints} points`);
              
              // Clear buffer for next sweep
              dataBuffer = [];
              textBuffer = "";
              
              // Request next data if still scanning (slower refresh for visible changes)
              if (scanningActiveRef.current) {
                await new Promise(r => setTimeout(r, 800));
                await writer.write(encoder.encode("data 0\r"));
              }
            } else if (hasPrompt && dataBuffer.length < 100 && dataBuffer.length > 0) {
              console.log(`[tinySA] Prompt found but only ${dataBuffer.length} points, requesting again...`);
              dataBuffer = [];
              textBuffer = "";
              if (scanningActiveRef.current) {
                await writer.write(encoder.encode("data 0\r"));
              }
            }
          }
        } catch (err) {
          if (scanningActiveRef.current) {
            console.error("[tinySA] Read error:", err);
          }
        } finally {
          console.log("Read loop ended");
        }
      };
      
      // Start the read loop (runs in background)
      readLoop();
      
    } catch (err) {
      console.error("Start scan error:", err);
      if (err.message?.includes("locked")) {
        toast.error("El puerto está bloqueado. Desconecta y reconecta el tinySA.");
      } else {
        toast.error("Error al iniciar escaneo: " + err.message);
      }
      scanningActiveRef.current = false;
      setSerialScanning(false);
    }
  }, [serialConnected]);

  // Stop tinySA scanning
  const stopSerialScan = useCallback(async () => {
    scanningActiveRef.current = false;
    setSerialScanning(false);
    
    try {
      if (serialReaderRef.current) {
        await serialReaderRef.current.cancel();
        serialReaderRef.current.releaseLock();
        serialReaderRef.current = null;
      }
      
      if (serialWriterRef.current) {
        serialWriterRef.current.releaseLock();
        serialWriterRef.current = null;
      }
      
      toast.info("Escaneo detenido");
    } catch (err) {
      console.error("Stop scan error:", err);
    }
  }, []);

  // ============ COORDINATION MODE FUNCTIONS ============
  
  // Start coordination mode - begins accumulating scan data for analysis
  const startCoordinationMode = useCallback(() => {
    if (!serialScanning) {
      toast.error("Inicia el escaneo primero");
      return;
    }
    
    setCoordinationMode(true);
    setCoordinationData({
      startTime: new Date(),
      scanCount: 0,
      frequencyStats: {},
      markers: [],
      analysis: null,
    });
    
    toast.success("Modo Coordinación iniciado", {
      description: "Acumulando datos para análisis..."
    });
  }, [serialScanning]);
  
  // Process scan data in coordination mode
  const processCoordinationScan = useCallback((scanDataPoints) => {
    if (!coordinationMode) return;
    
    setCoordinationData(prev => {
      const newStats = { ...prev.frequencyStats };
      const newMarkers = [...prev.markers];
      const now = new Date();
      
      // Process each frequency point
      scanDataPoints.forEach(point => {
        const freqKey = point.frequency.toFixed(1);
        
        if (!newStats[freqKey]) {
          newStats[freqKey] = {
            sum: 0,
            count: 0,
            max: -150,
            min: 0,
            values: [],
            lastValue: null,
          };
        }
        
        const stat = newStats[freqKey];
        stat.sum += point.level;
        stat.count += 1;
        stat.max = Math.max(stat.max, point.level);
        stat.min = Math.min(stat.min, point.level);
        stat.values.push(point.level);
        
        // Keep only last 100 values for variance calculation
        if (stat.values.length > 100) {
          stat.values = stat.values.slice(-100);
        }
        
        // Detect significant changes (potential interference)
        if (stat.lastValue !== null) {
          const change = point.level - stat.lastValue;
          // Spike detection: sudden increase > 15dB
          if (change > 15 && point.level > -70) {
            newMarkers.push({
              time: now,
              type: 'spike',
              frequency: point.frequency,
              level: point.level,
              description: `Pico detectado: ${point.frequency.toFixed(1)} MHz @ ${point.level.toFixed(0)} dBm`
            });
          }
        }
        stat.lastValue = point.level;
      });
      
      return {
        ...prev,
        scanCount: prev.scanCount + 1,
        frequencyStats: newStats,
        markers: newMarkers.slice(-50), // Keep last 50 markers
      };
    });
  }, [coordinationMode]);
  
  // Effect to process coordination data when tinySAData updates
  useEffect(() => {
    if (coordinationMode && tinySAData.length > 0) {
      processCoordinationScan(tinySAData);
    }
  }, [tinySAData, coordinationMode, processCoordinationScan]);
  
  // Analyze coordination data and generate recommendations
  const analyzeCoordinationData = useCallback(() => {
    const stats = coordinationData.frequencyStats;
    const frequencies = Object.keys(stats).map(Number).sort((a, b) => a - b);
    
    if (frequencies.length === 0) {
      toast.error("No hay suficientes datos para analizar");
      return null;
    }
    
    // Calculate stats for each frequency
    const analyzed = frequencies.map(freq => {
      const freqKey = freq.toFixed(1);
      const stat = stats[freqKey];
      const avg = stat.sum / stat.count;
      
      // Calculate variance
      const variance = stat.values.reduce((sum, val) => {
        return sum + Math.pow(val - avg, 2);
      }, 0) / stat.values.length;
      const stdDev = Math.sqrt(variance);
      
      return {
        frequency: freq,
        average: avg,
        max: stat.max,
        min: stat.min,
        stdDev: stdDev,
        samples: stat.count,
      };
    });
    
    // Categorize frequencies
    const clean = []; // Low noise, stable
    const occupied = []; // High noise, stable
    const unstable = []; // Variable (potential intermittent interference)
    
    analyzed.forEach(f => {
      if (f.average < -85 && f.stdDev < 5) {
        clean.push(f);
      } else if (f.average > -60) {
        occupied.push(f);
      } else if (f.stdDev > 8) {
        unstable.push(f);
      }
    });
    
    // Find best frequency ranges (consecutive clean frequencies)
    const cleanRanges = [];
    let rangeStart = null;
    let rangeFreqs = [];
    
    clean.sort((a, b) => a.frequency - b.frequency).forEach((f, i, arr) => {
      if (rangeStart === null) {
        rangeStart = f.frequency;
        rangeFreqs = [f];
      } else if (f.frequency - arr[i-1].frequency <= 1.0) {
        rangeFreqs.push(f);
      } else {
        if (rangeFreqs.length >= 3) {
          cleanRanges.push({
            start: rangeStart,
            end: rangeFreqs[rangeFreqs.length - 1].frequency,
            width: rangeFreqs[rangeFreqs.length - 1].frequency - rangeStart,
            avgNoise: rangeFreqs.reduce((s, f) => s + f.average, 0) / rangeFreqs.length,
          });
        }
        rangeStart = f.frequency;
        rangeFreqs = [f];
      }
    });
    // Don't forget the last range
    if (rangeFreqs.length >= 3) {
      cleanRanges.push({
        start: rangeStart,
        end: rangeFreqs[rangeFreqs.length - 1].frequency,
        width: rangeFreqs[rangeFreqs.length - 1].frequency - rangeStart,
        avgNoise: rangeFreqs.reduce((s, f) => s + f.average, 0) / rangeFreqs.length,
      });
    }
    
    // Sort ranges by width (wider is better)
    cleanRanges.sort((a, b) => b.width - a.width);
    
    const analysis = {
      duration: (new Date() - coordinationData.startTime) / 1000, // seconds
      totalScans: coordinationData.scanCount,
      totalFrequencies: frequencies.length,
      summary: {
        cleanCount: clean.length,
        occupiedCount: occupied.length,
        unstableCount: unstable.length,
      },
      cleanFrequencies: clean.slice(0, 20), // Top 20 cleanest
      occupiedFrequencies: occupied,
      unstableFrequencies: unstable,
      recommendedRanges: cleanRanges.slice(0, 5), // Top 5 ranges
      markers: coordinationData.markers,
      timestamp: new Date().toISOString(),
    };
    
    setCoordinationData(prev => ({ ...prev, analysis }));
    
    return analysis;
  }, [coordinationData]);
  
  // Stop coordination mode and generate analysis
  const stopCoordinationMode = useCallback(() => {
    const analysis = analyzeCoordinationData();
    setCoordinationMode(false);
    
    if (analysis) {
      toast.success("Análisis completado", {
        description: `${analysis.summary.cleanCount} frecuencias limpias encontradas`
      });
    }
    
    return analysis;
  }, [analyzeCoordinationData]);
  
  // Reset coordination data
  const resetCoordinationData = useCallback(() => {
    setCoordinationData({
      startTime: null,
      scanCount: 0,
      frequencyStats: {},
      markers: [],
      analysis: null,
    });
  }, []);

  const value = {
    // HID State (Relacart)
    hidConnected,
    hidDevice,
    hidDeviceInfo,
    hidScanning,
    isHIDSupported,
    connectHID,
    disconnectHID,
    startHIDScan,
    stopHIDScan,
    sendHIDCommand,
    
    // Serial State (tinySA)
    serialConnected,
    serialPort,
    serialDeviceInfo,
    serialScanning,
    isSerialSupported,
    tinySAData,
    connectSerial,
    disconnectSerial,
    startSerialScan,
    stopSerialScan,
    
    // Shared State
    activeDevice,
    scanData,
    rawDataLog,
    
    // Scan History (persists across navigation)
    lastScanResult,
    scanHistory,
    saveScanToHistory,
    loadScanFromHistory,
    clearScanHistory,
    
    // Coordination Mode
    coordinationMode,
    coordinationData,
    startCoordinationMode,
    stopCoordinationMode,
    resetCoordinationData,
    analyzeCoordinationData,
    
    // Shared Actions
    clearScanData,
    importScanData,
  };

  return (
    <USBConnectionContext.Provider value={value}>
      {children}
    </USBConnectionContext.Provider>
  );
}

export function useUSBConnection() {
  const context = useContext(USBConnectionContext);
  if (!context) {
    throw new Error("useUSBConnection must be used within USBConnectionProvider");
  }
  return context;
}
